// Session PJ2: book a site survey visit from the project. One visit covers the surveys ticked here: the site survey, each registered
// lift on the project and any lift added by its name. The platform makes the surveys that do not exist yet, makes ONE visit and puts
// the engineer and the day on it through the same route the Schedule page uses, so the engineer is told, their calendar is updated
// and the capacity, leave and certification warnings are shown here as they are there.
//
// Press once to check the booking (nothing is written), press again to book it.
import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type ProjectSurveyBookResult, type ProjectSurveysView, type ScheduleEngineer } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { formatDate } from "../../../lib/format";
import { MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN, ModalShell } from "../../../components/ModalShell";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

/** Today as YYYY-MM-DD in the browser's own zone, for the date box's earliest day. */
function todayYmd(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function BookVisitModal({
  view,
  only,
  onClose,
  onBooked,
}: {
  view: ProjectSurveysView;
  /** Tick only this survey (a visit booked from one survey's row). Absent: every survey not yet submitted. */
  only?: string;
  onClose: () => void;
  onBooked: (result: ProjectSurveyBookResult) => void;
}) {
  const live = view.surveys.filter((s) => s.supersededById == null);
  const done = (s: (typeof live)[number]) => s.status === "submitted" || s.status === "reviewed";
  const assetIdsWithSurvey = new Set(live.map((s) => s.assetId).filter((x): x is string => !!x));
  const unsurveyedLifts = view.project.lifts.filter((l) => !assetIdsWithSurvey.has(l.assetId));

  const [ticked, setTicked] = useState<Set<string>>(() => new Set(live.filter((s) => !done(s) && (only ? s.id === only : true)).map((s) => s.id)));
  const [tickedLifts, setTickedLifts] = useState<Set<string>>(() => new Set(only ? [] : unsurveyedLifts.map((l) => l.assetId)));
  const [labels, setLabels] = useState<string[]>([]);
  const [labelDraft, setLabelDraft] = useState("");
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [engineerId, setEngineerId] = useState("");
  const [date, setDate] = useState("");
  const [accessNote, setAccessNote] = useState("");
  const [checked, setChecked] = useState<ProjectSurveyBookResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.scheduling
      .engineers()
      .then((e) => {
        if (alive) setEngineers(e);
      })
      .catch(() => {
        if (alive) setError("Could not load the engineer list. Please try again.");
      });
    return () => {
      alive = false;
    };
  }, []);

  // Any change to the booking puts the check back to the first step.
  const touch = <T,>(set: (v: T) => void) => (v: T) => {
    setChecked(null);
    set(v);
  };

  const body = useMemo(
    () => ({
      surveyIds: [...ticked],
      addAssetIds: [...tickedLifts],
      addLabels: labels,
      engineerId,
      date,
      ...(accessNote.trim() ? { accessNote: accessNote.trim() } : {}),
    }),
    [ticked, tickedLifts, labels, engineerId, date, accessNote],
  );

  const nothing = ticked.size === 0 && tickedLifts.size === 0 && labels.length === 0;
  const blocker = !view.project.hasAddress
    ? "Add the site address to the project first."
    : nothing
      ? "Tick at least one survey, or add a lift."
      : !engineerId
        ? "Choose the engineer."
        : !date
          ? "Choose the day."
          : null;

  function addLabel() {
    const l = labelDraft.trim();
    if (!l) return;
    if (!labels.some((x) => x.toLowerCase() === l.toLowerCase())) touch(setLabels)([...labels, l]);
    setLabelDraft("");
  }

  async function press() {
    if (blocker) return;
    setBusy(true);
    setError(null);
    try {
      if (!checked) {
        setChecked(await api.projectSurveysOffice.book(view.project.id, { ...body, preview: true }));
      } else {
        const result = await api.projectSurveysOffice.book(view.project.id, { ...body, preview: false });
        onBooked(result);
      }
    } catch (e) {
      // A refusal carries a plain sentence (no address, a lift name already used, a day in the past).
      setError(e instanceof ApiError ? friendlyApiError(e, "Could not book the visit.") : "Could not book the visit.");
    } finally {
      setBusy(false);
    }
  }

  const warnings = checked ? [checked.warnings.capacityWarning, checked.warnings.leaveWarning, checked.warnings.certificationWarning].filter(Boolean) : [];

  return (
    <ModalShell ariaLabel="Book a survey visit" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-5 max-h-[90vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">Book a survey visit</h2>
      <p className="mt-1 text-sm text-ele-body">
        One visit covers the surveys you tick. The engineer is told and the visit goes on their calendar.
      </p>
      {!view.project.hasAddress ? (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">Add the site address to the project first.</p>
      ) : (
        <p className="mt-2 text-sm text-ele-body">
          At {[view.project.siteName, view.project.siteAddress, view.project.sitePostcode].filter(Boolean).join(", ")}
        </p>
      )}

      <fieldset className="mt-4">
        <legend className={labelCls}>Surveys this visit covers</legend>
        <ul className="space-y-1">
          {live.map((s) => (
            <li key={s.id}>
              <label className="flex min-h-9 max-sm:min-h-11 items-center gap-2 text-sm text-ele-body">
                <input
                  type="checkbox"
                  checked={ticked.has(s.id)}
                  disabled={done(s)}
                  onChange={() => {
                    setChecked(null);
                    setTicked((t) => {
                      const n = new Set(t);
                      if (n.has(s.id)) n.delete(s.id);
                      else n.add(s.id);
                      return n;
                    });
                  }}
                />
                <span>
                  {s.kind === "site" ? "Site survey" : s.label}
                  {done(s) ? <span className="text-xs text-ele-muted"> (already submitted: start a resurvey to visit it again)</span> : null}
                </span>
              </label>
            </li>
          ))}
          {unsurveyedLifts.map((l) => (
            <li key={l.assetId}>
              <label className="flex min-h-9 max-sm:min-h-11 items-center gap-2 text-sm text-ele-body">
                <input
                  type="checkbox"
                  checked={tickedLifts.has(l.assetId)}
                  onChange={() => {
                    setChecked(null);
                    setTickedLifts((t) => {
                      const n = new Set(t);
                      if (n.has(l.assetId)) n.delete(l.assetId);
                      else n.add(l.assetId);
                      return n;
                    });
                  }}
                />
                <span>
                  {l.label} <span className="text-xs text-ele-muted">(on the project, no survey yet)</span>
                </span>
              </label>
            </li>
          ))}
          {live.every((s) => s.kind !== "site") ? <li className="text-xs text-ele-muted">The site survey will be made for this visit.</li> : null}
          {labels.map((l) => (
            <li key={l} className="flex items-center justify-between gap-2 text-sm text-ele-body">
              <span>{l} <span className="text-xs text-ele-muted">(new lift)</span></span>
              <button type="button" className="text-xs text-ele-muted underline max-sm:min-h-11" onClick={() => touch(setLabels)(labels.filter((x) => x !== l))} aria-label={`Remove ${l}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <input
            className={inputCls}
            aria-label="Name of a lift to add"
            placeholder="Add a lift by name, for example Lift 3 or Block B"
            value={labelDraft}
            maxLength={120}
            onChange={(e) => setLabelDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addLabel();
              }
            }}
          />
          <button type="button" className={MODAL_SECONDARY_BTN} onClick={addLabel} disabled={!labelDraft.trim()}>
            Add lift
          </button>
        </div>
      </fieldset>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="survey-engineer">Engineer</label>
          <select id="survey-engineer" className={inputCls} value={engineerId} onChange={(e) => touch(setEngineerId)(e.target.value)}>
            <option value="">Choose...</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="survey-date">Day</label>
          <input id="survey-date" type="date" className={inputCls} min={todayYmd()} value={date} onChange={(e) => touch(setDate)(e.target.value)} />
        </div>
      </div>
      <div className="mt-3">
        <label className={labelCls} htmlFor="survey-access">Access note for the engineer</label>
        <textarea id="survey-access" rows={2} className={inputCls} maxLength={2000} value={accessNote} onChange={(e) => touch(setAccessNote)(e.target.value)} placeholder="Who to ask for, key holder, parking" />
      </div>

      {checked ? (
        <div className="mt-4 rounded-ele border border-ele-card-border p-3 text-sm text-ele-body" role="status" data-booking-check>
          <p className="text-ele-strong">
            {checked.engineerName} will be told about a visit on {formatDate(checked.date)} at {checked.address}.
          </p>
          <p className="mt-1">Covers: {checked.surveys.join(", ")}.</p>
          {checked.willCreate && checked.willCreate.length > 0 ? <p className="mt-1">Made for this visit: {checked.willCreate.join(", ")}.</p> : null}
          {warnings.map((w) => (
            <p key={w} className="mt-1 text-[var(--ele-amber-text)]">{w}</p>
          ))}
          <p className="mt-2 text-xs text-ele-muted">Nothing has been booked yet. Press Book the visit to book it.</p>
        </div>
      ) : null}

      {blocker && !checked ? <p className="mt-3 text-xs text-ele-muted">{blocker}</p> : null}
      {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p> : null}

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void press()} disabled={busy || blocker !== null}>
          {busy ? (checked ? "Booking..." : "Checking...") : checked ? "Book the visit" : "Check the booking"}
        </button>
      </div>
    </ModalShell>
  );
}
